import { getDetectedCity, getDetectedCountry } from '@/services/utils/userLocation.js';

export const formatLocalDate = (date) => {
  if (!(date instanceof Date)) return date;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const extractDateRange = (dateInput) => {
  if (!dateInput) {
    return { startDate: null, endDate: null };
  }

  const start = dateInput.startDate || dateInput.start_date;
  const end = dateInput.endDate || dateInput.end_date;

  if (!start || !end) {
    return { startDate: null, endDate: null };
  }

  return {
    startDate: formatLocalDate(start),
    endDate: formatLocalDate(end),
  };
};

const resolveDestination = (destination) => {
  if (!destination) return undefined;
  if (typeof destination === 'string') return destination;

  return destination.name
    || destination.formatted_address
    || destination.description
    || String(destination);
};

const normalizeActivity = (activity) => {
  if (typeof activity === 'string') return activity;

  return activity.activity || activity.label || String(activity);
};

const getWhenType = (whereWhen, startDate, endDate) => {
  if (whereWhen === 'today') return 'today';
  if (whereWhen === 'date-selected' && startDate && endDate) return 'specific_dates';
  return 'not_sure';
};

export const createTripSearchRequest = (searchData, location = {}) => {
  const { startDate, endDate } = extractDateRange(searchData.date);
  const budgetAmount = searchData.budget && searchData.budget !== '' ? Number(searchData.budget) : null;
  const whenType = getWhenType(searchData.whereWhen, startDate, endDate);
  const departureCity = searchData.destination?.departure_city || location.city || getDetectedCity();
  const country = location.country || getDetectedCountry();

  // NOTE: user_location.coordinates must represent the user's ORIGIN/current location,
  // NOT the destination they want to visit. The TripFinder build mode form does not yet
  // have a dedicated departure coordinates field, so this stays null for now.
  // Blindly passing searchData.destination.coordinates here would incorrectly set the
  // user's origin to their destination — a subtle but critical semantic bug.
  // TODO: Add departure-location coordinates tracking to the TripFinder form state,
  //       then source them here (e.g. from a "fromLocation" field with lat/lng).
  return {
    mode: searchData.iWant === 'build' ? 'build' : 'recommend',
    search_scope: searchData.go === 'local' ? 'local' : 'international',
    when_type: whenType,
    user_location: {
      city: departureCity,
      country,
      state: null,
      coordinates: null,
      timezone: null,
    },
    ...(searchData.iWant === 'build' && searchData.destination
      ? { destination: resolveDestination(searchData.destination) }
      : {}),
    ...(whenType === 'specific_dates' && startDate && endDate
      ? {
          date_range: {
            start_date: startDate,
            end_date: endDate,
          },
        }
      : {}),
    num_adults: searchData.travelers?.adults || 1,
    num_children: searchData.travelers?.children || 0,
    num_infants: 0,
    with_pets: searchData.travelers?.pets > 0 || false,
    ...(budgetAmount
      ? {
          budget_range: {
            min_amount: null,
            max_amount: budgetAmount,
            currency: 'USD',
            per_person: false,
          },
        }
      : {}),
    themes: searchData.themes || [],
    activities: (searchData.activities || []).map(normalizeActivity),
    travel_methods: searchData.travelMethods || [],
    accommodation_types: searchData.accommodations || [],
    special_requirements: searchData.specialRequirements || [],
    trip_direction: searchData.tripDirection || null,
    ...(searchData.regions?.length ? { preferred_regions: searchData.regions } : {}),
    ...((searchData.tripTypes?.length || searchData.discoverTripTypes?.length)
      ? {
          travel_types: [
            ...(searchData.tripTypes || []),
            ...(searchData.discoverTripTypes || []),
          ],
        }
      : {}),
    ...(searchData.distanceType && searchData.travelDistance
      ? { max_travel_distance_km: Math.round(searchData.travelDistance * 80) }
      : {}),
    ...(searchData.whoIsGoing ? { who_is_going: searchData.whoIsGoing } : {}),
    max_results: 12,
  };
};
